/**
 * Central API Client
 *
 * Single source of truth for all backend API communication.
 *
 * Responsibilities:
 * - Authentication (Bearer token from authService)
 * - Response unwrapping ({ success, data } -> data)
 * - Error normalization (ApiError class)
 * - Network error handling
 *
 * Backend Response Contract:
 * - Success: { success: true, data: any, message?: string, meta?: object }
 * - Error:   { success: false, error: string, code?: string, details?: string }
 */

import authService from './authService';
import { supabase } from './supabaseClient';
import config from '../config';

/**
 * Custom error class for API errors
 * Provides structured error information for consistent handling
 */
/**
 * Publica la señal de consumo que escucha BillingContext.
 *
 * Vive aquí y se EXPORTA porque hay dos clientes HTTP: éste y el XHR de
 * `threadService.uploadMessageWithProgress`, que no pasa por aquí. Mientras sólo
 * lo hacía apiClient, un envío CON adjuntos que chocaba con el tope devolvía su
 * 402 y nadie levantaba el aviso (#1299). Si añades un tercer cliente, importa
 * esta funcion; no la reescribas.
 *
 * @param {number} level - 80 | 100 (centinelas, no porcentajes)
 */
export const dispatchUsageSignal = (level) => {
  if (!Number.isFinite(level)) return;
  window.dispatchEvent(new CustomEvent('billing:usage', { detail: { level } }));
};

/**
 * #1298 — la prueba gratuita ha terminado: el backend corta con 402 `trial_expired`.
 * <TrialExpiredListener /> (App.jsx) lleva a /billing/required, donde puede elegir plan.
 * Mismo motivo que `dispatchUsageSignal`: la usan TODOS los clientes HTTP (apiClient y el
 * XHR de adjuntos), o un envío con adjuntos al acabar la prueba volvería a ser un clic
 * que no hace nada. Si añades un cliente, importa esta función; no la reescribas.
 *
 * @param {string} [endpoint] - solo para el trazado
 */
export const dispatchTrialExpired = (endpoint) => {
  window.dispatchEvent(new CustomEvent('billing:trial-expired', { detail: { endpoint, timestamp: Date.now() } }));
};

/**
 * #1294 — algo se ha parado (o no ha empezado) por saldo. La escucha BillingContext, que
 * alimenta el aviso único `CreditStopNotice` y refresca el consumo.
 *
 * Motivos: credit_exhausted | debt_limit_reached | trial_expired | trial_doc_limit.
 * `trial_doc_limit` NO es «sin crédito»: la prueba tiene 30 documentos y ya los ha usado, así
 * que no levanta la señal de consumo al 100 %. Mismo contrato que las dos de arriba: si añades
 * un cliente, importa esta función.
 *
 * @param {{reason: string, surface?: string, docsUsed?: number, docsMax?: number}} detail
 */
export const dispatchCreditStop = (detail) => {
  if (!detail?.reason) return;
  // Ni el tope de documentos, ni un saldo que no se pudo comprobar, ni una renovación sin cobrar
  // son «plan agotado».
  if (!['trial_doc_limit', 'balance_unavailable', 'renewal_unpaid'].includes(detail.reason)) dispatchUsageSignal(100);
  window.dispatchEvent(new CustomEvent('billing:stop', { detail: { ...detail, at: Date.now() } }));
};

/**
 * Traduce un 402 de la puerta a las señales de arriba. Un único sitio para los dos clientes
 * HTTP (apiClient y el XHR de adjuntos).
 */
/**
 * #1294 — mensaje y código de un error de API. El 503 `TRIAL_REQUIRES_STREAMING` trae su
 * propio texto para el usuario (pasajero o de la cuenta): se usa como mensaje, y el código
 * pasa a ser el error, para que quien lo pinte (envío, reintento) no enseñe el código crudo.
 * Para el resto, el motivo de un 402 (p. ej. trial_doc_limit) viaja como `code`.
 */
export const apiErrorParts = (status, json) => {
  if (json?.error === 'TRIAL_REQUIRES_STREAMING' && json.message) {
    return { message: json.message, code: 'TRIAL_REQUIRES_STREAMING' };
  }
  return { message: json?.error || `Error ${status}`, code: json?.code || json?.reason || null };
};

export const dispatchBillingError = (status, json, endpoint) => {
  if (status !== 402 || !json) return;
  if (json.error === 'trial_expired') {
    dispatchTrialExpired(endpoint);
    return;
  }
  // #759 R: renovación sin cobrar, o la suscripción de Stripe ya cancelada. <RenewalListener />
  // lleva a la pantalla que toca.
  if (json.error === 'renewal_unpaid' || json.error === 'subscription_canceled') {
    window.dispatchEvent(new CustomEvent('billing:renewal', { detail: { error: json.error, endpoint } }));
    return;
  }
  if (json.error === 'usage_limit_exceeded') {
    dispatchCreditStop({
      reason: json.reason || 'credit_exhausted',
      surface: endpoint,
      docsUsed: json.docsUsed,
      docsMax: json.docsMax,
      // La puerta dice si es trial: el aviso elige su botón sin depender de que
      // /api/billing/usage haya respondido.
      isTrial: typeof json.trial === 'boolean' ? json.trial : undefined,
    });
  }
};

export class ApiError extends Error {
  /**
   * @param {string} message - Human-readable error message
   * @param {number} status - HTTP status code (0 for network errors)
   * @param {string|null} code - Machine-readable error code
   * @param {any} details - Additional error details
   */
  constructor(message, status, code = null, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Check if this is a network/connectivity error
   */
  isNetworkError() {
    return this.status === 0 || this.code === 'NETWORK_ERROR';
  }

  /**
   * Check if the request was aborted by the client-side timeout
   */
  isTimeout() {
    return this.code === 'TIMEOUT';
  }

  /**
   * Check if this is an authentication error
   */
  isAuthError() {
    return this.status === 401 || this.code === 'NO_AUTH_TOKEN';
  }

  /**
   * Check if this is a validation error
   */
  isValidationError() {
    return this.status === 400;
  }

  /**
   * Check if resource was not found
   */
  isNotFound() {
    return this.status === 404;
  }
}

/**
 * Tope por defecto de una petición JSON (#915). Sin él, una conexión que el
 * servidor acepta y nunca contesta deja el `await` colgado para siempre: el
 * `finally` de quien llama no corre y un diálogo que bloquea su cierre mientras
 * espera (DeletePlanDialog, EmptyPlanDialog) solo sale recargando la página.
 *
 * Las subidas con FormData NO llevan tope por defecto: su duración depende del
 * tamaño del fichero y de la red del usuario, y cortarlas rompería subidas
 * legítimas. Quien quiera acotarlas pasa `timeoutMs`.
 */
export const DEFAULT_TIMEOUT_MS = 60000;

const timeoutError = () => new ApiError(
  'El servidor está tardando demasiado en responder',
  0,
  'TIMEOUT'
);

/**
 * API Client class
 * Handles all HTTP communication with the backend
 */
class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Core request method - all other methods delegate to this
   *
   * @param {string} endpoint - API endpoint (e.g., '/api/threads')
   * @param {object} options - Request options
   * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
   * @param {any} options.body - Request body (object, FormData, or null)
   * @param {object} options.headers - Additional headers
   * @param {object} options.params - Query params (serialized as ?k=v); undefined values are skipped
   * @param {boolean} options.authenticated - Whether to include auth token (default: true)
   * @param {number|null} options.timeoutMs - Abort after N ms (default: DEFAULT_TIMEOUT_MS,
   *   or none for FormData bodies; null disables it)
   * @returns {Promise<any>} - Unwrapped response data
   * @throws {ApiError} - On any error (network, auth, API error, timeout)
   */
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      params = null,
      authenticated = true,
      _isRetry = false,
    } = options;
    const timeoutMs = 'timeoutMs' in options
      ? options.timeoutMs
      : (body instanceof FormData ? null : DEFAULT_TIMEOUT_MS);

    let url = `${this.baseUrl}${endpoint}`;
    if (params && typeof params === 'object') {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null) qs.append(k, String(v));
      }
      const qsStr = qs.toString();
      if (qsStr) url += (endpoint.includes('?') ? '&' : '?') + qsStr;
    }
    const requestHeaders = { ...headers };

    // Add auth token if required
    if (authenticated) {
      const token = await authService.getToken();
      if (!token) {
        throw new ApiError(
          'No hay sesión activa',
          401,
          'NO_AUTH_TOKEN'
        );
      }
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Handle body serialization
    // - FormData: let browser set Content-Type with boundary
    // - Object: serialize to JSON
    // - null: no body
    let requestBody = body;
    if (body !== null && !(body instanceof FormData)) {
      requestHeaders['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(body);
    }

    // The timer also covers reading the body (response.json()), not just the headers.
    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: requestBody,
        signal: controller?.signal,
      });

      // Handle empty responses (204 No Content)
      if (response.status === 204) {
        return null;
      }

      // Propagate usage warnings so BillingContext can react without polling
      const usageWarning = response.headers.get('X-Usage-Warning');
      if (usageWarning) {
        dispatchUsageSignal(parseInt(usageWarning, 10));
      }

      // Parse response body
      let json;
      try {
        json = await response.json();
      } catch (parseError) {
        // The body read was cut by the timeout, not a malformed body
        if (controller?.signal.aborted) throw timeoutError();
        // Response wasn't JSON - might be an error page
        if (!response.ok) {
          throw new ApiError(
            `Error del servidor (${response.status})`,
            response.status,
            'INVALID_RESPONSE'
          );
        }
        // Success but not JSON is unexpected
        throw new ApiError(
          'Respuesta inesperada del servidor',
          response.status,
          'INVALID_RESPONSE'
        );
      }

      // Handle error responses (HTTP error or success: false)
      if (!response.ok || json.success === false) {
        dispatchBillingError(response.status, json, endpoint);

        const parts = apiErrorParts(response.status, json);
        const apiError = new ApiError(
          parts.message,
          response.status,
          parts.code,
          json.details || null
        );

        // On 401, attempt one token refresh and retry.
        // Guards: only when authenticated, only one retry, only when session exists.
        if (response.status === 401 && authenticated && !_isRetry) {
          const { data, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && data?.session?.access_token) {
            return this.request(endpoint, { ...options, _isRetry: true });
          }
          // Refresh failed — session is dead, sign out to force login
          await supabase.auth.signOut();
          window.location.href = '/login';
        }

        // On 451 TERMS_NOT_ACCEPTED — backend refuses to serve a user who has
        // not accepted T&C. We dispatch a custom event instead of doing
        // `window.location.href` (hard nav) which used to interrupt in-flight
        // fetches AND show a Suspense fallback "flash". A `<TermsRedirectListener />`
        // mounted in App.jsx subscribes and uses SPA navigate. We still throw
        // so callers don't treat the request as successful.
        // PRP flash-diagnostics-onboarding-flow (2026-05-26, D2).
        if (response.status === 451 && json.code === 'TERMS_NOT_ACCEPTED') {
          if (typeof window !== 'undefined' && window.location.pathname !== '/onboarding/terms') {
            window.dispatchEvent(new CustomEvent('auth:terms-required', {
              detail: { endpoint, timestamp: Date.now() }
            }));
          }
        }

        // On 403 ACCOUNT_SUSPENDED / ACCOUNT_BLOCKED — the account-status gate
        // refused the request (#559). Same CustomEvent + SPA-navigate approach as
        // the terms gate above, for the same reason (no hard nav, no Suspense
        // flash). <AccountStatusListener /> in App.jsx subscribes.
        //
        // Discriminate by the PAIR status + code, never by 403 alone: 403 is also
        // what requireAdmin (roleAuth.js) and the BYOK guards
        // (PLAN_PROVIDES_KEYS) return, and neither should bounce the user to the
        // inactive-account screen. `ApiError` already exposes `code` as a
        // first-class property, so there is nothing to parse.
        if (response.status === 403
            && (json.code === 'ACCOUNT_SUSPENDED' || json.code === 'ACCOUNT_BLOCKED')) {
          if (typeof window !== 'undefined' && window.location.pathname !== '/account/inactive') {
            window.dispatchEvent(new CustomEvent('auth:account-inactive', {
              detail: { code: json.code, endpoint, timestamp: Date.now() }
            }));
          }
        }

        throw apiError;
      }

      // SUCCESS: Return unwrapped data
      return json.data;

    } catch (error) {
      // Re-throw ApiErrors as-is
      if (error instanceof ApiError) {
        throw error;
      }

      if (controller?.signal.aborted) {
        throw timeoutError();
      }

      // Handle network/fetch errors
      if (error.name === 'TypeError' && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message === 'Load failed'
      )) {
        throw new ApiError(
          'No se puede conectar con el servidor',
          0,
          'NETWORK_ERROR'
        );
      }

      // Handle JSON parse errors (shouldn't reach here due to try/catch above)
      if (error instanceof SyntaxError) {
        throw new ApiError(
          'Respuesta inválida del servidor',
          500,
          'PARSE_ERROR'
        );
      }

      // Unknown errors - wrap in ApiError
      throw new ApiError(
        error.message || 'Error desconocido',
        500,
        'UNKNOWN_ERROR'
      );
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional options
   */
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {any} body - Request body (object or FormData)
   * @param {object} options - Additional options
   */
  post(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {any} body - Request body
   * @param {object} options - Additional options
   */
  put(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {any} body - Request body
   * @param {object} options - Additional options
   */
  patch(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional options
   */
  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(config.apiUrl);
export default apiClient;
